import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { toast } from 'sonner';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import {
	invalidateGetPersonalApiKeys,
	IPersonalApiKey,
	useDeletePersonalApiKeyMutation,
	useGetPersonalApiKeys,
	usePostPersonalApiKeyMutation,
} from '../../../../queries';
import { IPostPersonalApiKeyInput } from '../../../../queries/PersonalApiKeys/usePostPersonalApiKey';
import { API_KEY_STATUS_DISPLAY, deriveApiKeyStatus } from '../../../../utils/apiKeyStatus';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '../../../components/DataGrid';
import {
	ConfirmationDialog,
	DefaultDeleteConfirmationOptions,
	IConfirmationDialogOption,
} from '../../../components/ConfirmationDialog';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { ClipboardIcon2 } from '../../../components/svgs/icons/ClipboardIcon';
import { SimpleTooltip } from '../../../components/Tooltip';
import { WarningIcon } from '../../../components/svgs/icons/WarningIcon';
import { Button } from '../../../components-v2/Button';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../components-v2/Dropdown';
import { Pill } from '../../../components-v2/Pill';
import { baseStyleSheet } from '../../../styles/styles';
import { GeneratePersonalApiKeyDialog } from './GeneratePersonalApiKeyDialog';
import { styleSheet } from './styles';

export const PersonalApiKeys = () => {
	const [showGenerateDialog, setShowGenerateDialog] = React.useState(false);
	const { logApiError } = useEventLogging('PersonalApiKeys');
	const errorMessages = useErrorMessages();
	const [keyToDelete, setKeyToDelete] = React.useState<IPersonalApiKey | null>(null);

	const userSession = useUserSession();

	const apiKeysQuery = useGetPersonalApiKeys({
		enabled: Boolean(userSession.account.features.publicApi?.enabled),
		refetchOnWindowFocus: false,
	});

	const createPersonalApiKey = usePostPersonalApiKeyMutation();

	const deletePersonalApiKey = useDeletePersonalApiKeyMutation({
		onSuccess: () => {
			toast.success('API Key has been deleted');
		},
		onError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
			logApiError('DeletePersonalApiKey-Error', error);
		},
		// Invalidate on settle (success or error) so a flaky-network delete that succeeded
		// server-side still reconciles the list, since this query doesn't refetch on focus.
		onSettled: () => {
			invalidateGetPersonalApiKeys();
		},
	});

	const onDeleteConfirmationRequestClose = (result?: IConfirmationDialogOption<boolean>, cancel?: boolean) => {
		const key = keyToDelete;
		setKeyToDelete(null);
		if (key && result?.isDestructive && !cancel) {
			deletePersonalApiKey.mutate(key.id);
		}
	};

	if (apiKeysQuery.isPending) {
		return <LoadingSpinner type='large' className={css(baseStyleSheet.absoluteCenter)} />;
	}

	const saveNewKey = (input: IPostPersonalApiKeyInput) => {
		const created = createPersonalApiKey.mutateAsync(input);

		// Initiate the clipboard write synchronously within the user gesture so Safari allows it;
		// the ClipboardItem data resolves once the API responds.
		const copied = navigator.clipboard.write([
			new ClipboardItem({
				'text/plain': created.then(newKey => new Blob([newKey.key ?? ''], { type: 'text/plain' })),
			}),
		]);
		// The mutation chain below owns all messaging; swallow here so an API failure doesn't
		// surface as a misleading "failed to copy" toast or an unhandled rejection.
		copied.catch((): void => {});

		created
			.then(newKey => {
				setShowGenerateDialog(false);
				invalidateGetPersonalApiKeys();
				if (!newKey.key) {
					toast.error('Your key was created but could not be copied. Please delete it and try again.');
					return;
				}
				copied
					.then(() => toast.success('New key has been copied to your clipboard'))
					.catch(() => toast.error('Your key was created but could not be copied to your clipboard.'));
			})
			.catch((error: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(error);
				logApiError('CreatePersonalApiKey-Error', error);
			});
	};

	const copyKey = (id: string) => {
		const fetched = userSession.webServiceHelper.callAsync<string>(
			Api.ImpersonationBroker.composeApiUrl({ urlPath: `personalapikeys/${id}/key` }),
			'GET'
		);

		// Initiate the clipboard write synchronously within the user gesture so Safari allows it;
		// the ClipboardItem data resolves once the API responds.
		const copied = navigator.clipboard.write([
			new ClipboardItem({
				'text/plain': fetched.then(key => new Blob([key], { type: 'text/plain' })),
			}),
		]);
		// The fetch chain below owns error logging; swallow here so a fetch failure doesn't also
		// surface as a clipboard toast or an unhandled rejection.
		copied.catch((): void => {});

		fetched
			.then(() =>
				copied
					.then(() => toast.success('Key has been copied to your clipboard'))
					.catch(() => toast.error('Could not copy the key to your clipboard.'))
			)
			.catch((error: Api.IOperationResultNoValue) => {
				logApiError('CopyPersonalApiKey-Error', error);
				toast.error('Failed to copy key to clipboard');
			});
	};

	const deleteKey = (key: IPersonalApiKey) => {
		setKeyToDelete(key);
	};

	const apiDocsUrl = `${import.meta.env.VITE_API_URL?.replace(/\/$/, '') ?? ''}/docs`;

	return (
		<div className={css(styleSheet.container)}>
			<header className={css(styleSheet.header)}>
				<section className={css(styleSheet.headerCopy)}>
					<h1 className={css(baseStyleSheet.breadcrumbTitle)}>Personal API Keys</h1>
					<small>
						Generate and manage your personal API keys to use in scripts or third-party apps interacting with the
						Levitate public API. These keys act on behalf of your user account, with only the access and permissions
						that you have.
					</small>
				</section>

				<section className={css(styleSheet.headerActions)}>
					<Button variant='primary' onClick={() => setShowGenerateDialog(true)}>
						Create API Key
					</Button>
				</section>
			</header>
			<div className={css(styleSheet.card)}>
				<DataGrid>
					<DataGridColumns>
						<DataGridColumn>Description</DataGridColumn>
						<DataGridColumn>Key</DataGridColumn>
						<DataGridColumn>Added</DataGridColumn>
						<DataGridColumn>Last Used</DataGridColumn>
						<DataGridColumn>Scopes</DataGridColumn>
						<DataGridColumn>Expiration</DataGridColumn>
						<DataGridColumn>Actions</DataGridColumn>
					</DataGridColumns>
					<DataGridRows>
						{apiKeysQuery.data?.map((apiKey: IPersonalApiKey) => {
							const lastUsedDate = apiKey.lastUsedDate ? moment(apiKey.lastUsedDate).format('MM/DD/YYYY') : 'Never';
							const status = API_KEY_STATUS_DISPLAY[deriveApiKeyStatus(apiKey.expirationDate)];

							return (
								<DataGridRow key={`personal-api-keys-${apiKey.id}`}>
									<DataGridRowItem>{apiKey.description}</DataGridRowItem>
									<DataGridRowItem>
										<span className={css(styleSheet.keyCell)}>
											<span>**** **** **** ****</span>
											<SimpleTooltip title='Copy to clipboard'>
												<Button
													variant='link'
													size='icon'
													onClick={() => copyKey(apiKey.id)}
													aria-label='Copy to clipboard'
												>
													<ClipboardIcon2 />
												</Button>
											</SimpleTooltip>
										</span>
									</DataGridRowItem>
									<DataGridRowItem>{moment(apiKey.creationDate).format('MM/DD/YYYY')}</DataGridRowItem>
									<DataGridRowItem>{lastUsedDate}</DataGridRowItem>
									<DataGridRowItem>
										{apiKey.scopes?.length ? (
											<span className={css(styleSheet.scopeCell)}>
												{apiKey.scopes.map(scope => (
													<span key={scope}>{scope}</span>
												))}
											</span>
										) : (
											'—'
										)}
									</DataGridRowItem>
									<DataGridRowItem>
										<span className={css(styleSheet.expirationCell)}>
											<span className={css(styleSheet.expirationDate)}>
												{apiKey.expirationDate ? moment(apiKey.expirationDate).format('MM/DD/YYYY') : 'Never'}
											</span>
											<Pill variant='label' color={status.color} label={status.label} />
										</span>
									</DataGridRowItem>
									<DataGridRowItem>
										<Dropdown>
											<DropdownEllipsisTrigger />
											<DropdownContent>
												<DropdownItem onClick={() => deleteKey(apiKey)}>Delete</DropdownItem>
											</DropdownContent>
										</Dropdown>
									</DataGridRowItem>
								</DataGridRow>
							);
						})}
						{(apiKeysQuery.data?.length ?? 0) < 1 ? (
							<DataGridRow>
								<DataGridRowItem fullColSpan>No data found</DataGridRowItem>
							</DataGridRow>
						) : null}
					</DataGridRows>
				</DataGrid>
			</div>

			<GeneratePersonalApiKeyDialog
				isOpen={showGenerateDialog}
				onOpenChange={setShowGenerateDialog}
				onGenerate={saveNewKey}
			/>

			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: !!keyToDelete,
					onRequestClose: onDeleteConfirmationRequestClose,
				}}
				options={DefaultDeleteConfirmationOptions}
				title={
					<span>
						{`Are you sure you want to delete the "${keyToDelete?.description || ''}" key?`}
						<br />
						<b>The key will no longer work.</b>
					</span>
				}
			/>

			<footer className={css(styleSheet.docsFooter)}>
				<span>Need help?</span>
				<Button variant='link' asChild>
					<a href={apiDocsUrl} target='_blank' rel='noopener noreferrer'>
						View the API documentation
					</a>
				</Button>
			</footer>
		</div>
	);
};
